import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, Button, TextField, FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Snackbar, IconButton, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, TablePagination, useTheme, alpha, CircularProgress, Tooltip, Alert, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { Close as CloseIcon, Edit as EditIcon, Delete as DeleteIcon, Download as DownloadIcon, Add as AddIcon, Upload as UploadIcon } from '@mui/icons-material';
import { downloadCsvTemplate, TIME_TRANSACTION_TEMPLATE_COLUMNS } from '../utils/csvTemplate';
import { normalizeDate } from '../components/bulk/helpers';
import ToolbarButton from '../components/ToolbarButton';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';
import { AccessTime as TimeIcon, Receipt as ReceiptIcon, ListAlt as ListIcon, Tune as TuneIcon, CalendarViewWeek as CalendarIcon } from '@mui/icons-material';
import { WaveLoading } from '../components/Loader';
import TimesheetGrid from '../components/TimesheetGrid';

// Loaded on demand: SheetJS is ~150 kB gzipped and only needed for bulk entry.
const BulkGrid = React.lazy(() => import('../components/bulk/BulkGrid'));

const TimeTransactions = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const { canPerformActionByRoute, moduleIdMap, loading: permissionsLoading } = usePermissions();
  
  // Permission checks
  const canAdd = user?.role === 'admin' || canPerformActionByRoute('/time-transactions', 'Add');
  const canEdit = user?.role === 'admin' || canPerformActionByRoute('/time-transactions', 'Edit');
  const canDelete = user?.role === 'admin' || canPerformActionByRoute('/time-transactions', 'Delete');
  const canView = user?.role === 'admin' || canPerformActionByRoute('/time-transactions', 'View');
  // Anyone who can view the page can also download what they see. The backend
  // already scopes /time-transactions to the caller (team members see only
  // their own entries), so this is safe — the export is just "save what's on
  // screen as CSV", inheriting whatever row-level access the API allows.
  const canExport = canView;
  
  const [editingId, setEditingId] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null); // Track the entry being edited
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [loadingTransactionTypes, setLoadingTransactionTypes] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 0 });
  const [form, setForm] = useState({ taskId: '', date: '', hours: '', transactions: '', transactionType: '', fileName: '', userId: '' });
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  // 'list' = existing entries table; 'timesheet' = grid for logging many days at once
  const [viewMode, setViewMode] = useState('list');
  const [dateFilters, setDateFilters] = useState(() => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    return { startDate: `${y}-${m}-01`, endDate: `${y}-${m}-${d}` };
  });
  const [dateErrors, setDateErrors] = useState({ startDate: '', endDate: '' });
  const [formDateError, setFormDateError] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [bulkGridOpen, setBulkGridOpen] = useState(false);
  const [addEntryOpen, setAddEntryOpen] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    taskName: '',
    projectName: '',
    employeeName: [], // multi-select: array of employee names
    serviceId: '',    // global admins only — filters by the entry's project service
  });
  // Global admin = admin with no serviceid (sees every service)
  const isGlobalAdmin = user?.role === 'admin' && !user?.serviceid;
  const [services, setServices] = useState([]);
  useEffect(() => {
    if (!isGlobalAdmin) return;
    fetch(`${process.env.REACT_APP_API_URL}/service-management/services`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setServices(Array.isArray(data) ? data : []))
      .catch(() => setServices([]));
  }, [isGlobalAdmin]);

  // Field visibility (hidden fields) based on service + module mapping
  const [hiddenFields, setHiddenFields] = useState(new Set());

  // Services where File Name / Description and Transactions are optional.
  // Mirrors OPTIONAL_FILE_AND_TX_SERVICE_IDS in the backend's routes/timeTransactions.js.
  //   30 = FW AI SW, 37 = Corporate Software
  const OPTIONAL_FILE_AND_TX_SERVICE_IDS = [30, 37];
  // Decided by the selected task's PROJECT service (so someone working across
  // services gets that project's rules); falls back to the user's own service
  // before a task is picked.
  const selectedTaskForRules = tasks.find(t => t.id === parseInt(form.taskId, 10))
    || (editingEntry?.task?.id === parseInt(form.taskId, 10) ? editingEntry.task : null);
  const rulesServiceId = selectedTaskForRules?.Project?.serviceId ?? user?.serviceid;
  const fileAndTxOptional = OPTIONAL_FILE_AND_TX_SERVICE_IDS.includes(Number(rulesServiceId));

  // Helper to check if a field should be hidden for the current user/service in this module
  const isFieldHidden = (fieldName) => hiddenFields.has(fieldName);

  // Fetch field visibility config for Time & Transactions when user or module map changes
  useEffect(() => {
    const fetchFieldVisibility = async () => {
      try {
        if (!user || !user.serviceid) return;

        const moduleName = 'Time & Transactions';
        const moduleId = moduleIdMap[moduleName];
        if (!moduleId) return;

        const token = localStorage.getItem('token');
        if (!token) return;

        const url = `${process.env.REACT_APP_API_URL}/service-management/field-visibility/${user.serviceid}/${moduleId}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          // If there is no config or endpoint error, fall back to showing all fields
          console.error('Failed to fetch field visibility config for Time & Transactions');
          setHiddenFields(new Set());
          return;
        }

        const data = await res.json();
        const fields = Array.isArray(data?.hiddenFields)
          ? data.hiddenFields
          : [];

        setHiddenFields(
          new Set(
            fields
              .filter((name) => typeof name === 'string' && name.trim() !== '')
              .map((name) => name.trim())
          )
        );
      } catch (err) {
        console.error('Error fetching field visibility for Time & Transactions:', err);
        setHiddenFields(new Set());
      }
    };

    fetchFieldVisibility();
  }, [user, moduleIdMap]);

  // Filter tasks based on user role and selected employee
  const getFilteredTasks = () => {
    let filteredTasks = [];
    
    if (user.role === 'team_member') {
      // Employees only see tasks assigned to them
      filteredTasks = tasks.filter(task => 
        task.assignees && task.assignees.some(assignee => assignee.id === user.id)
      );
    } else {
      // Admin, PM, TL, CMO: filter by selected employee if one is selected
      if (form.userId) {
        const selectedUserId = parseInt(form.userId, 10);
        // Filter tasks assigned to the selected employee
        // Check both assignedTo field and assignees array
        filteredTasks = tasks.filter(task => {
          // Check if task is assigned to the selected employee via assignedTo field
          if (task.assignedTo === selectedUserId) {
            return true;
          }
          // Check if task is assigned to the selected employee via assignees array
          if (task.assignees && Array.isArray(task.assignees)) {
            return task.assignees.some(assignee => assignee.id === selectedUserId);
          }
          // Also check if assignees is an object with id property
          if (task.assignees && task.assignees.id === selectedUserId) {
            return true;
          }
          return false;
        });
        console.log('[TimeTransactions] Filtered tasks for employee', selectedUserId, ':', filteredTasks.length, 'tasks');
      } else {
        // No employee selected, show all tasks
        filteredTasks = tasks;
      }
    }
    
    // Filter by selected project
    if (selectedProjectId) {
      filteredTasks = filteredTasks.filter(task => task.Project?.id === parseInt(selectedProjectId, 10) || task.projectId === parseInt(selectedProjectId, 10));
    }

    // If editing an entry and its task is not in the list, add it
    if (editingEntry?.task && form.taskId) {
      const taskExists = filteredTasks.some(t => t.id === parseInt(form.taskId, 10));
      if (!taskExists && editingEntry.task) {
        // Add the task from the entry to the options
        filteredTasks = [...filteredTasks, editingEntry.task];
      }
    }

    return filteredTasks;
  };

  // Get unique projects from tasks for the project dropdown
  const getProjectOptions = () => {
    const projectMap = new Map();
    const baseTasks = user.role === 'team_member'
      ? tasks.filter(task => task.assignees && task.assignees.some(a => a.id === user.id))
      : form.userId
        ? tasks.filter(task => {
            const uid = parseInt(form.userId, 10);
            return task.assignedTo === uid || (task.assignees && task.assignees.some(a => a.id === uid));
          })
        : tasks;
    baseTasks.forEach(task => {
      if (task.Project && task.Project.id && !projectMap.has(task.Project.id)) {
        projectMap.set(task.Project.id, {
          id: task.Project.id,
          name: task.Project.name,
          status: task.Project.status || null,
        });
      }
    });

    // For non team-members, also include projects the user is directly associated with
    // (e.g. Project Manager / Team Leader / Requester) even if no task is assigned yet.
    // When an employee is selected by an admin/PM/TL, restrict to that employee's associations.
    if (user.role !== 'team_member' && Array.isArray(projects)) {
      const targetUserId = form.userId ? parseInt(form.userId, 10) : user.id;
      projects.forEach(p => {
        if (!p || !p.id || projectMap.has(p.id)) return;
        const isManager = p.managerId === targetUserId;
        const isTeamLeader = p.teamLeaderId === targetUserId;
        const isRequester = p.requesterId === targetUserId;
        const isTeamMember = Array.isArray(p.teamMembers)
          && p.teamMembers.some(m => (m?.id ?? m) === targetUserId);
        // Admin/CMO with no employee filter see every project; otherwise gate by association.
        const adminLike = !form.userId && (user.role === 'admin' || user.role === 'cmo');
        if (adminLike || isManager || isTeamLeader || isRequester || isTeamMember) {
          projectMap.set(p.id, { id: p.id, name: p.name, status: p.status || null });
        }
      });
    }

    return Array.from(projectMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  };
  // Export current entries to CSV
  const handleExportCsv = () => {
    // Export what the user currently sees on the page — i.e. honour the
    // Project / Task / Employee filters at the top of the page (date range
    // is already applied at fetch time on `entries`).
    const rowsToExport = filteredEntries;
    if (!rowsToExport.length) {
      setSnackbar({ open: true, message: 'No entries to export for the current filters' });
      return;
    }

    // Helper function to escape CSV values and handle special characters
    const escapeCsvValue = (value) => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      // If the value contains comma, quote, or newline, wrap it in quotes and escape internal quotes
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const headers = ['Employee','Project','Task','File Name','Date','Hours','Transactions','Type'];
    const csvRows = [headers.join(',')];

    let totalHours = 0;
    let totalTransactions = 0;
    rowsToExport.forEach((e) => {
      const hours = parseFloat(e.hours) || 0;
      const txns = parseFloat(e.transactions) || 0;
      totalHours += hours;
      totalTransactions += txns;
      const row = [
        escapeCsvValue(e.user?.name || ''),
        escapeCsvValue(e.task?.Project?.name || ''),
        escapeCsvValue(e.task?.title || ''),
        escapeCsvValue(e.fileName || ''),
        escapeCsvValue(e.date),
        escapeCsvValue(e.hours),
        escapeCsvValue(e.transactions),
        escapeCsvValue(e.transactionType || '')
      ];
      csvRows.push(row.join(','));
    });

    // Totals row (Employee/Project/Task/File Name blank, "Total" under Date)
    csvRows.push([
      '', '', '', '',
      'Total',
      escapeCsvValue(totalHours.toFixed(2)),
      escapeCsvValue(totalTransactions),
      '',
    ].join(','));

    const csvString = csvRows.join('\n');
    // Add BOM for proper UTF-8 encoding
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvString], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Generate filename with date range if filters are applied
    let filename = `time_entries_${new Date().toISOString().split('T')[0]}`;
    if (dateFilters.startDate && dateFilters.endDate) {
      filename += `_${dateFilters.startDate}_to_${dateFilters.endDate}`;
    } else if (dateFilters.startDate) {
      filename += `_from_${dateFilters.startDate}`;
    } else if (dateFilters.endDate) {
      filename += `_to_${dateFilters.endDate}`;
    }
    filename += '.csv';
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // fetch entries function (moved outside useEffect for pagination access)
  const fetchEntries = async (page = 1, limit = 10000) => {
    try {
      setLoading(true);
      let url = `${process.env.REACT_APP_API_URL}/time-transactions`;
      const params = new URLSearchParams();
      
      if (dateFilters.startDate) {
        params.append('startDate', dateFilters.startDate);
      }
      if (dateFilters.endDate) {
        params.append('endDate', dateFilters.endDate);
      }
      // Always send page & limit to avoid backend defaults
      params.append('page', page);
      params.append('limit', limit);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (!res.ok) throw new Error('Failed to fetch entries');
      const data = await res.json();
      setEntries(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to fetch entries' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // fetch tasks for selection
    const fetchTasks = async () => {
      try {
        let endpoint;
        if (user.role === 'team_member') {
          endpoint = `${process.env.REACT_APP_API_URL}/tasks/my-tasks`;
        } else {
          // For admin/PM/TL, fetch all tasks without pagination limit
          endpoint = `${process.env.REACT_APP_API_URL}/tasks?limit=10000&page=1`;
        }
        const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({ message: 'Unknown error' }));
          console.error('[TimeTransactions] Failed to fetch tasks:', res.status, errorData);
          throw new Error(errorData.message || `Failed to fetch tasks: ${res.status}`);
        }
        const data = await res.json();
        console.log('[TimeTransactions] Fetched tasks:', Array.isArray(data) ? data.length : data.data?.length || 0);
        setTasks(Array.isArray(data) ? data : data.data || []);
      } catch (err) {
        console.error('[TimeTransactions] Error fetching tasks:', err);
        setSnackbar({ open: true, message: `Failed to fetch tasks: ${err.message}` });
      }
    };
    
    // fetch users for admin/pm/tl
    const fetchUsers = async () => {
      const token = localStorage.getItem('token');
      try {
        let url = `${process.env.REACT_APP_API_URL}/users/for-project-management`;
        
        const res = await fetch(url, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        
        if (!res.ok) throw new Error('Failed to fetch users');
        const data = await res.json();
        setUsers(Array.isArray(data) ? data : data.data || []);
      } catch (err) { 
        console.error('Error fetching users:', err);
        // Fallback to active users
        try {
          const fallbackUrl = `${process.env.REACT_APP_API_URL}/users/active`;
          const fallbackRes = await fetch(fallbackUrl, { 
            headers: { Authorization: `Bearer ${token}` } 
          });
          if (fallbackRes.ok) {
            const fallbackData = await fallbackRes.json();
            setUsers(Array.isArray(fallbackData) ? fallbackData : fallbackData.data || []);
          }
        } catch (fallbackErr) {
          console.error('Fallback user fetch failed:', fallbackErr);
          setUsers([]);
        }
      }
    };
    // fetch projects so PM/TL/Requester see their associated projects
    // in the Add Time Entry dropdown even when no task exists yet.
    const fetchProjects = async () => {
      if (user.role === 'team_member') return; // team members rely on assigned tasks
      const token = localStorage.getItem('token');
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/projects?page=1&limit=10000`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Failed to fetch projects: ${res.status}`);
        const data = await res.json();
        setProjects(Array.isArray(data) ? data : data.data || []);
      } catch (err) {
        console.error('[TimeTransactions] Error fetching projects:', err);
        setProjects([]);
      }
    };

    fetchTasks();
    fetchUsers();
    fetchProjects();
  }, [user]);

  useEffect(() => {
    fetchEntries(1, 10000);
  }, [user, dateFilters]);

  // Fetch transaction types when dialog opens with a pre-selected task (for editing)
  useEffect(() => {
    if (addEntryOpen && form.taskId) {
      const selectedTask = tasks.find(t => t.id === parseInt(form.taskId, 10));
      if (selectedTask?.Project?.serviceId) {
        console.log('[TimeTransactions] Dialog opened with task, fetching transaction types for serviceId:', selectedTask.Project.serviceId);
        fetchTransactionTypes(selectedTask.Project.serviceId);
      }
    }
  }, [addEntryOpen, form.taskId, tasks]);

  const fetchTransactionTypes = async (serviceId) => {
    try {
      setLoadingTransactionTypes(true);
      if (!serviceId) {
        console.log('[TimeTransactions] No serviceId provided, clearing transaction types');
        setTransactionTypes([]);
        return;
      }
      console.log('[TimeTransactions] Fetching transaction types for serviceId:', serviceId);
      const url = `${process.env.REACT_APP_API_URL}/transaction-types?serviceId=${serviceId}`;
      console.log('[TimeTransactions] Fetching from URL:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      console.log('[TimeTransactions] Response status:', response.status);
      console.log('[TimeTransactions] Response ok:', response.ok);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('[TimeTransactions] Error response:', errorData);
        throw new Error(errorData.message || 'Failed to fetch transaction types');
      }
      
      const data = await response.json();
      console.log('[TimeTransactions] Received data:', data);
      console.log('[TimeTransactions] Data type:', Array.isArray(data) ? 'array' : typeof data);
      console.log('[TimeTransactions] Data length:', Array.isArray(data) ? data.length : 'N/A');
      
      const transactionTypesArray = Array.isArray(data) ? data : [];
      console.log('[TimeTransactions] Setting transaction types:', transactionTypesArray);
      setTransactionTypes(transactionTypesArray);
    } catch (error) {
      console.error('[TimeTransactions] Error fetching transaction types:', error);
      console.error('[TimeTransactions] Error details:', error.message);
      setTransactionTypes([]);
    } finally {
      setLoadingTransactionTypes(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedForm = { ...form, [name]: value };
    
    // Validate date - prevent future dates
    if (name === 'date' && value) {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to compare dates only
      selectedDate.setHours(0, 0, 0, 0);
      
      if (selectedDate > today) {
        setFormDateError('Cannot log entries for future dates. Please select today or a past date.');
        return; // Don't update form if future date
      } else {
        setFormDateError(''); // Clear error if valid date
      }
    }
    
    // When task changes, fetch transaction types based on task's project serviceId
    if (name === 'taskId' && value) {
      const taskIdNum = typeof value === 'string' ? parseInt(value, 10) : value;
      const selectedTask = tasks.find(t => t.id === taskIdNum || t.id === value);
      console.log('[TimeTransactions] Task selected:', taskIdNum);
      console.log('[TimeTransactions] Selected task object:', selectedTask);
      console.log('[TimeTransactions] Task transactionType:', selectedTask?.transactionType);
      console.log('[TimeTransactions] Task Project:', selectedTask?.Project);
      console.log('[TimeTransactions] Task Project serviceId:', selectedTask?.Project?.serviceId);
      
      if (selectedTask?.Project?.serviceId) {
        console.log('[TimeTransactions] Task has project with serviceId, fetching transaction types');
        fetchTransactionTypes(selectedTask.Project.serviceId);
        // Auto-populate transactionType from task if it exists, otherwise clear it
        updatedForm.transactionType = selectedTask?.transactionType && selectedTask.transactionType.trim() !== '' 
          ? selectedTask.transactionType.trim() 
          : '';
      } else {
        console.log('[TimeTransactions] Task does not have project serviceId, clearing transaction types');
        setTransactionTypes([]);
        updatedForm.transactionType = '';
      }
    }
    
    setForm(updatedForm);
  };

  // Handle edit button click
  const handleEdit = (entry) => {
    console.log('[TimeTransactions] Editing entry:', entry);
    console.log('[TimeTransactions] Entry task:', entry.task);
    console.log('[TimeTransactions] Entry task Project:', entry.task?.Project);
    console.log('[TimeTransactions] Entry task Project serviceId:', entry.task?.Project?.serviceId);
    
    // Store the entry being edited
    setEditingEntry(entry);
    
    setForm({
      taskId: entry.taskId,
      date: entry.date,
      hours: entry.hours,
      transactions: entry.transactions,
      transactionType: entry.transactionType,
      fileName: entry.fileName || '',
      userId: entry.user?.id || '',
    });
    setEditingId(entry.id);
    
    // Fetch transaction types for the task's project service when editing
    // First try from entry.task.Project.serviceId
    if (entry.task?.Project?.serviceId) {
      console.log('[TimeTransactions] Fetching transaction types from entry.task.Project.serviceId:', entry.task.Project.serviceId);
      fetchTransactionTypes(entry.task.Project.serviceId);
    } else {
      // Fallback: find the task in the tasks list to get its project serviceId
      const taskFromList = tasks.find(t => t.id === entry.taskId);
      if (taskFromList?.Project?.serviceId) {
        console.log('[TimeTransactions] Fetching transaction types from tasks list, serviceId:', taskFromList.Project.serviceId);
        fetchTransactionTypes(taskFromList.Project.serviceId);
      } else {
        console.log('[TimeTransactions] Could not find serviceId for task, clearing transaction types');
        setTransactionTypes([]);
      }
    }
  };

  // Handle delete button click
  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!entryToDelete) return;

    setDeleting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/time-transactions/${entryToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error('Failed to delete entry');
      setEntries(entries.filter((e) => e.id !== entryToDelete.id));
      setSnackbar({ open: true, message: 'Entry deleted successfully!' });
      setDeleteOpen(false);
      setEntryToDelete(null);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: err.message });
      setDeleteOpen(false);
      setEntryToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteOpen(false);
    setEntryToDelete(null);
  };

  // ---- Bulk grid (paste / import / type) --------------------------------------
  const restrictedToSelf = ['team_member', 'art', 'copy', 'research', 'partner', 'QC', 'qc'].includes(user?.role);
  const bulkColumns = useMemo(() => {
    const emails = users.map((u) => u.email).filter(Boolean).sort();
    const projectNames = [...new Set(tasks.map((t) => t.Project?.name).filter(Boolean))].sort();
    const lc = (x) => String(x || '').trim().toLowerCase();
    // Only the tasks of the project chosen on that row (all tasks until a project is picked).
    const tasksFor = (values) => [...new Set(tasks
      .filter((t) => !values.projectName || lc(t.Project?.name) === lc(values.projectName))
      .map((t) => t.title).filter(Boolean))].sort();
    const cols = [
      { key: 'projectName', label: 'Project', type: 'lookup', required: true, width: 220, lookupOptions: projectNames, aliases: ['project name'] },
      { key: 'taskTitle', label: 'Task', type: 'lookup', required: true, width: 240, lookupOptions: tasksFor, aliases: ['task title', 'task name', 'activity'] },
      { key: 'date', label: 'Date', type: 'date', required: true, width: 140, aliases: ['day', 'work date', 'entry date'], placeholder: 'dd/mm/yyyy' },
      { key: 'hours', label: 'Hours', type: 'number', required: true, width: 90, aliases: ['hrs', 'time', 'duration'] },
      { key: 'transactions', label: 'Transactions', type: 'number', width: 110, aliases: ['tx', 'count', 'units'], help: 'Required except for FW AI SW and Corporate Software projects.' },
      { key: 'transactionType', label: 'Transaction type', type: 'text', width: 150, aliases: ['type', 'format'], help: 'Must be one of the types set up for the project\'s service.' },
      { key: 'fileName', label: 'File / description', type: 'text', width: 220, aliases: ['file', 'file name', 'description', 'notes'], help: 'Required except for FW AI SW and Corporate Software projects.' },
    ];
    if (!restrictedToSelf) cols.unshift({ key: 'employeeEmail', label: 'Employee', type: 'lookup', required: true, width: 230, lookupOptions: emails, emails: true, aliases: ['employee email', 'user', 'email', 'email id', 'resource', 'person', 'name', 'staff', 'member'], help: 'Whose time this is. Must be in your team.' });
    return cols;
  }, [users, tasks, restrictedToSelf]);

  const validateTimeRow = useCallback((v, ctx) => {
    const errors = {};
    const norm = (x) => String(x || '').toLowerCase().replace(/\s*,\s*/g, ',').replace(/\s+/g, ' ').trim();
    let userId = null; let employeeEmailRaw = null;
    if (restrictedToSelf) userId = user?.id;
    else if (v.employeeEmail) {
      const u = users.find((usr) => norm(usr.email) === norm(v.employeeEmail));
      if (u) userId = u.id;
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.employeeEmail.trim())) errors.employeeEmail = 'Not an email address';
      else employeeEmailRaw = v.employeeEmail.trim();
    }
    let taskId = null;
    if (v.projectName && v.taskTitle) {
      const match = tasks.find((t) => norm(t.title) === norm(v.taskTitle) && norm(t.Project?.name) === norm(v.projectName));
      if (!match) errors.taskTitle = `No task "${v.taskTitle}" in project "${v.projectName}"`;
      else if ((match.Project?.status || '').toLowerCase() === 'completed') errors.projectName = 'Project is completed - no new time entries';
      else taskId = match.id;
    }
    const hours = parseFloat(v.hours);
    if (v.hours && (isNaN(hours) || hours < 0)) errors.hours = 'Must be a positive number';
    else if (v.hours && hours > 24) errors.hours = 'More than 24 hours in a day';
    const transactions = v.transactions ? parseInt(v.transactions, 10) : 0;
    if (v.transactions && (isNaN(transactions) || transactions < 0)) errors.transactions = 'Must be a positive whole number';
    const date = v.date ? normalizeDate(v.date, ctx.dayFirst) : null;
    const now = new Date();
    const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`; // local date, not UTC
    if (date && date > todayKey) errors.date = 'In the future';
    return {
      errors,
      payload: { userId, employeeEmail: employeeEmailRaw, taskId, date, hours, transactions, transactionType: (v.transactionType || '').trim() || null, fileName: (v.fileName || '').trim() || null },
    };
  }, [users, tasks, restrictedToSelf, user?.id]);

  const timeDedupeKey = useCallback((v) => (v.taskTitle && v.date && v.hours ? [v.employeeEmail, v.projectName, v.taskTitle, v.date, v.hours].map((x) => String(x || '').trim().toLowerCase()).join('|') : null), []);

  // Sent in batches so a big sheet doesn't exceed the request-size limit; failed indexes are re-based to the whole list.
  const postBulkEntries = async (payloads, dryRun) => {
    const BATCH = 200;
    const created = []; const failed = [];
    for (let offset = 0; offset < payloads.length; offset += BATCH) {
      const batch = payloads.slice(offset, offset + BATCH);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/time-transactions/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ entries: batch, dryRun }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(`${data.message || `Bulk upload failed (HTTP ${response.status})`}${created.length ? `. ${created.length} entries were already saved.` : ''}`);
      (data.created || []).forEach((c) => created.push(c));
      (data.failed || []).forEach((f) => failed.push({ ...f, index: (f.index ?? 0) + offset }));
    }
    return { created, failed };
  };
  const bulkDryRun = (payloads) => postBulkEntries(payloads, true);
  const bulkSubmit = async (payloads) => {
    try { return await postBulkEntries(payloads, false); } finally { fetchEntries(1, 10000); }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      taskName: '',
      projectName: '',
      employeeName: [],
      serviceId: '',
    });
  };

  const handleAddEntryOpen = () => {
    setForm({ taskId: '', date: '', hours: '', transactions: '', transactionType: '', fileName: '', userId: '' });
    setEditingId(null);
    setEditingEntry(null); // Clear editing entry
    setSelectedProjectId('');
    setTransactionTypes([]);
    setFormDateError(''); // Clear date error
    setAddEntryOpen(true);
  };

  const handleAddEntryClose = () => {
    setAddEntryOpen(false);
    setForm({ taskId: '', date: '', hours: '', transactions: '', transactionType: '', fileName: '', userId: '' });
    setEditingId(null);
    setEditingEntry(null); // Clear editing entry
    setSelectedProjectId('');
    setTransactionTypes([]);
    setFormDateError(''); // Clear date error
  };

  const handleEditClick = (entry) => {
    setForm({
      taskId: entry.taskId || '',
      date: entry.date || '',
      hours: entry.hours || '',
      transactions: entry.transactions || '',
      transactionType: entry.transactionType || '',
      fileName: entry.fileName || '',
      userId: entry.userId || ''
    });
    setEditingId(entry.id);
    setAddEntryOpen(true);
    // Auto-select the project when editing
    const task = tasks.find(t => t.id === entry.taskId);
    setSelectedProjectId(task?.Project?.id || task?.projectId || '');
    // Fetch transaction types for the task's project service when editing
    if (task?.Project?.serviceId) {
      fetchTransactionTypes(task.Project.serviceId);
    }
  };

  // Filter entries based on current filters
  const filteredEntries = entries.filter(entry => {
    // Task Name filter
    if (filters.taskName && entry.task?.title && !entry.task.title.toLowerCase().includes(filters.taskName.toLowerCase())) {
      return false;
    }
    
    // Project Name filter
    if (filters.projectName && entry.task?.Project?.name && !entry.task.Project.name.toLowerCase().includes(filters.projectName.toLowerCase())) {
      return false;
    }
    
    // Service filter (global admins) — by the project the entry was logged against
    if (filters.serviceId) {
      const entryServiceId = entry.task?.Project?.serviceId
        ?? tasks.find((t) => t.id === entry.taskId)?.Project?.serviceId;
      if (String(entryServiceId) !== String(filters.serviceId)) return false;
    }

    // Employee Name filter (multi-select; only for PM, TL, Admin, CMO, View Only)
    if (Array.isArray(filters.employeeName) && filters.employeeName.length > 0) {
      if (!entry.user?.name) return false;
      const match = filters.employeeName.some(
        (n) => entry.user.name.toLowerCase() === String(n).toLowerCase()
      );
      if (!match) return false;
    }
    
    return true;
  });

  // Client-side pagination for filtered results
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedFilteredEntries = filteredEntries.slice(startIndex, endIndex);

  const handleSubmit = async () => {
    console.log('Submitting TimeEntry:', form);
    if (!isFieldHidden('taskId') && !form.taskId) {
      setSnackbar({ open: true, message: 'Please select a task.' });
      return false;
    }
    if (!isFieldHidden('date') && (!form.date || form.date === '')) {
      setSnackbar({ open: true, message: 'Date is required.' });
      return false;
    }
    // Validate date - prevent future dates
    if (!isFieldHidden('date') && form.date) {
      const selectedDate = new Date(form.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);
      if (selectedDate > today) {
        setFormDateError('Cannot log entries for future dates. Please select today or a past date.');
        setSnackbar({ open: true, message: 'Cannot log entries for future dates. Please select today or a past date.' });
        return false;
      }
    }
    if (!fileAndTxOptional && !isFieldHidden('fileName') && (!form.fileName || form.fileName.trim() === '')) {
      setSnackbar({ open: true, message: 'File Name / Description is required.' });
      return false;
    }
    if (
      !isFieldHidden('hours') &&
      (!form.hours || form.hours === '' || parseFloat(form.hours) < 0)
    ) {
      setSnackbar({
        open: true,
        message: 'Hours is required and must be a valid number (0 or greater).',
      });
      return false;
    }
    if (
      !fileAndTxOptional &&
      !isFieldHidden('transactions') &&
      (!form.transactions || form.transactions === '' || parseInt(form.transactions) < 0)
    ) {
      setSnackbar({
        open: true,
        message: 'Transactions is required and must be a valid number (0 or greater).',
      });
      return false;
    }
    setLoading(true);
    try {
      const url = editingId
        ? `${process.env.REACT_APP_API_URL}/time-transactions/${editingId}`
        : `${process.env.REACT_APP_API_URL}/time-transactions`;
      const method = editingId ? 'PUT' : 'POST';

      // Prepare payload, omitting hidden fields so backend does not receive unwanted data
      const payload = { ...form };

      // Default transactions to 0 if empty for services where it's optional
      if (fileAndTxOptional && (!payload.transactions || payload.transactions === '')) {
        payload.transactions = 0;
      }

      if (isFieldHidden('taskId')) {
        delete payload.taskId;
      }
      if (isFieldHidden('date')) {
        delete payload.date;
      }
      if (isFieldHidden('fileName')) {
        delete payload.fileName;
      }
      if (isFieldHidden('hours')) {
        delete payload.hours;
      }
      if (isFieldHidden('transactions')) {
        delete payload.transactions;
      }
      if (isFieldHidden('transactionType')) {
        delete payload.transactionType;
      }

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({
          ...payload,
          userId:
            (user.role === 'admin' || user.role === 'project_manager') && !isFieldHidden('userId')
              ? form.userId
              : undefined,
        }),
      });
      console.log('Response status:', res.status, 'OK:', res.ok);
      if (!res.ok) throw new Error('Failed to save entry');
      const data = await res.json();
      console.log('Response data:', data);
      // Update entries list
      setEntries((prev) => [data, ...prev.filter((e) => e.id !== data.id)]);
      setForm({ taskId: '', date: '', hours: '', transactions: '', transactionType: '', fileName: '', userId: '' });
      setEditingId(null);
      setFormDateError(''); // Clear date error on successful save
      setSnackbar({ open: true, message: 'Entry saved' });
      return true;
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: err.message });
      return false;
    } finally {
      setLoading(false);
    }
  };


  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <PageHeader
        title="Time & Transactions"
        actions={
          <ToggleButtonGroup size="small" exclusive value={viewMode} onChange={(_, v) => v && setViewMode(v)}>
            <ToggleButton value="list" sx={{ textTransform: 'none', px: 1.5 }}>List</ToggleButton>
            <ToggleButton value="timesheet" sx={{ textTransform: 'none', px: 1.5 }}>Timesheet</ToggleButton>
          </ToggleButtonGroup>
        }
      />

      {viewMode === 'timesheet' && (
        <TimesheetGrid
          user={user}
          tasks={tasks}
          entries={entries}
          onSaved={() => fetchEntries(1, 10000)}
          notify={(message) => setSnackbar({ open: true, message })}
        />
      )}

      {viewMode === 'list' && (<>

      {/* Filter Section — Notion-style soft bar, no "Filters" title */}
      <Box
        sx={{
          p: 2,
          mb: 3,
          bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafbfc',
          border: (t) => t.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
          borderRadius: 2.5,
        }}
      >
        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          {/* Date Range — primary filters, always visible */}
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={dateFilters.startDate}
            onChange={(e) => {
              const newStartDate = e.target.value;
              const newDateFilters = { ...dateFilters, startDate: newStartDate };
              const newDateErrors = { ...dateErrors };
              
              // Validate start date
              if (newStartDate && dateFilters.endDate && newStartDate > dateFilters.endDate) {
                newDateErrors.startDate = 'Start date cannot be after end date';
              } else {
                newDateErrors.startDate = '';
              }
              
              // Clear end date error if validation passes
              if (newStartDate && dateFilters.endDate && newStartDate <= dateFilters.endDate) {
                newDateErrors.endDate = '';
              }
              
              setDateFilters(newDateFilters);
              setDateErrors(newDateErrors);
            }}
            error={!!dateErrors.startDate}
            helperText={dateErrors.startDate}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
            inputProps={{
              max: dateFilters.endDate || undefined
            }}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={dateFilters.endDate}
            onChange={(e) => {
              const newEndDate = e.target.value;
              const newDateFilters = { ...dateFilters, endDate: newEndDate };
              const newDateErrors = { ...dateErrors };
              
              // Validate end date
              if (newEndDate && dateFilters.startDate && newEndDate < dateFilters.startDate) {
                newDateErrors.endDate = 'End date cannot be before start date';
              } else {
                newDateErrors.endDate = '';
              }
              
              // Clear start date error if validation passes
              if (newEndDate && dateFilters.startDate && newEndDate >= dateFilters.startDate) {
                newDateErrors.startDate = '';
              }
              
              setDateFilters(newDateFilters);
              setDateErrors(newDateErrors);
            }}
            error={!!dateErrors.endDate}
            helperText={dateErrors.endDate}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
            inputProps={{
              min: dateFilters.startDate || undefined
            }}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0 }} />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
            <ToolbarButton
              variant="outlined"
              disabled={loading}
              onClick={() => {
                const today = new Date();
                const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
                const fy = firstDay.getFullYear();
                const fm = String(firstDay.getMonth() + 1).padStart(2, '0');
                const ly = lastDay.getFullYear();
                const lm = String(lastDay.getMonth() + 1).padStart(2, '0');
                const ld = String(lastDay.getDate()).padStart(2, '0');
                setDateFilters({
                  startDate: `${fy}-${fm}-01`,
                  endDate: `${ly}-${lm}-${ld}`
                });
                setDateErrors({ startDate: '', endDate: '' });
              }}
            >
              Last Month
            </ToolbarButton>
            <ToolbarButton
              variant={showAdvancedFilters ? 'contained' : 'outlined'}
              startIcon={<TuneIcon />}
              onClick={() => setShowAdvancedFilters((v) => !v)}
            >
              More filters
              {(filters.projectName || filters.taskName || filters.employeeName.length > 0 || filters.serviceId) && ' •'}
            </ToolbarButton>
            {(filters.projectName || filters.taskName || filters.employeeName.length > 0 || filters.serviceId || dateFilters.startDate || dateFilters.endDate) && (
              <ToolbarButton
                variant="outlined"
                disabled={loading}
                onClick={() => {
                  clearFilters();
                  setDateFilters({ startDate: '', endDate: '' });
                  setDateErrors({ startDate: '', endDate: '' });
                }}
              >
                Clear All
              </ToolbarButton>
            )}
            {loading && <CircularProgress size={20} />}
          </Box>
        </Box>

        {(showAdvancedFilters || filters.projectName || filters.taskName || filters.employeeName.length > 0 || filters.serviceId) && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              alignItems: 'center',
              mt: 1.5,
              pt: 1.5,
              borderTop: (t) => t.palette.mode === 'dark' ? '1px dashed rgba(255,255,255,0.08)' : '1px dashed #e5e7eb',
            }}
          >
            <Autocomplete
              size="small"
              options={[...new Set(entries.map(e => e.task?.Project?.name).filter(Boolean))].sort()}
              value={filters.projectName}
              onChange={(event, newValue) => {
                handleFilterChange('projectName', newValue || '');
                if (newValue && filters.taskName) {
                  const taskBelongs = entries.some(e =>
                    e.task?.Project?.name === newValue && e.task?.title === filters.taskName
                  );
                  if (!taskBelongs) handleFilterChange('taskName', '');
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Project Name" placeholder="Any project" />
              )}
              sx={{ minWidth: 200 }}
              freeSolo
              clearOnEscape
            />
            <Autocomplete
              size="small"
              options={[...new Set(
                entries
                  .filter(e => !filters.projectName || e.task?.Project?.name === filters.projectName)
                  .map(e => e.task?.title)
                  .filter(Boolean)
              )].sort()}
              value={filters.taskName}
              onChange={(event, newValue) => handleFilterChange('taskName', newValue || '')}
              renderInput={(params) => (
                <TextField {...params} label="Task Name" placeholder="Any task" />
              )}
              sx={{ minWidth: 200 }}
              freeSolo
              clearOnEscape
            />
            {(user?.role === 'project_manager' || user?.role === 'team_leader' || user?.role === 'admin' || user?.role === 'cmo' || user?.role === 'view_only') && (
              <Autocomplete
                multiple
                size="small"
                options={[...new Set(entries.map((e) => e.user?.name).filter(Boolean))].sort()}
                value={filters.employeeName}
                onChange={(_, newValue) => handleFilterChange('employeeName', newValue)}
                sx={{ minWidth: 240 }}
                renderInput={(params) => (
                  <TextField {...params} label="Employee" placeholder={filters.employeeName.length ? '' : 'All employees'} />
                )}
              />
            )}
            {isGlobalAdmin && (
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Service</InputLabel>
                <Select
                  label="Service"
                  value={filters.serviceId}
                  onChange={(e) => handleFilterChange('serviceId', e.target.value)}
                >
                  <MenuItem value="">All services</MenuItem>
                  {services.map((svc) => (
                    <MenuItem key={svc.Id} value={String(svc.Id)}>{svc.Service}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        )}
      </Box>

      {/* Summary stat cards — Notion style, mirror the dashboard layout */}
      {filteredEntries.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2.5,
            mb: 3,
          }}
        >
          <StatCard
            label="Total Entries"
            value={filteredEntries.length.toLocaleString()}
            icon={<ListIcon />}
            accent="#3b82f6"
          />
          <StatCard
            label="Total Hours"
            value={filteredEntries.reduce((sum, entry) => sum + (parseFloat(entry.hours) || 0), 0).toFixed(2)}
            icon={<TimeIcon />}
            accent="#10b981"
          />
          <StatCard
            label="Total Transactions"
            value={filteredEntries.reduce((sum, entry) => sum + (parseInt(entry.transactions) || 0), 0).toLocaleString()}
            icon={<ReceiptIcon />}
            accent="#8b5cf6"
          />
        </Box>
      )}
      
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          mb: 3,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {canAdd && (
          <ToolbarButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddEntryOpen}
          >
            Add New Time Entry
          </ToolbarButton>
        )}
        {canAdd && (
          <ToolbarButton
            variant="outlined"
            startIcon={<CalendarIcon />}
            onClick={() => setViewMode('timesheet')}
          >
            Fill Timesheet (Day / Week / Month)
          </ToolbarButton>
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }} />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
          {canAdd && (
            <ToolbarButton
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => downloadCsvTemplate('time_transactions_template.csv', TIME_TRANSACTION_TEMPLATE_COLUMNS)}
              sx={{
                color: 'text.secondary',
                borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#e5e7eb',
                '&:hover': {
                  borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : '#cbd5e0',
                  backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafbfc',
                },
              }}
            >
              Download Template
            </ToolbarButton>
          )}
          {canAdd && (
            <ToolbarButton
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => setBulkGridOpen(true)}
              sx={{
                color: 'text.secondary',
                borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#e5e7eb',
                '&:hover': {
                  borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : '#cbd5e0',
                  backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafbfc',
                },
              }}
            >
              Add Many Entries
            </ToolbarButton>
          )}
          {canExport && (
            <ToolbarButton
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCsv}
              sx={{
                color: 'text.secondary',
                borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.16)' : '#e5e7eb',
                '&:hover': {
                  borderColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : '#cbd5e0',
                  backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafbfc',
                },
              }}
            >
              Download Report
            </ToolbarButton>
          )}
        </Box>
      </Box>
      {permissionsLoading ? (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 8,
            minHeight: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            borderRadius: 2,
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          <WaveLoading theme={theme} size="large" />
        </Box>
      ) : !canView ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            You don't have permission to view this data.
          </Typography>
        </Box>
      ) : (
      <>
      <TableContainer
        component={Paper}
        sx={{
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: 2,
          overflowX: 'auto',
          overflowY: 'hidden',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.1)' 
              : 'rgba(0, 0, 0, 0.1)',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.3)' 
              : 'rgba(0, 0, 0, 0.3)',
            borderRadius: 4,
            '&:hover': {
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.5)' 
                : 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <Table size="small" sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#1976d2' }}>
              {(user.role === 'admin' || user.role === 'project_manager' || user.role === 'team_leader' || user.role === 'cmo') && <TableCell sx={{ fontWeight: 'bold' }}>Employee</TableCell>}
              <TableCell sx={{ fontWeight: 'bold' }}>Project</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>File Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Task</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Hours</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Transactions</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell 
                  colSpan={(user.role === 'admin' || user.role === 'project_manager' || user.role === 'team_leader' || user.role === 'cmo' ? 8 : 7) + 1}
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    border: 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <WaveLoading theme={theme} size="large" />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                        fontWeight: 500
                      }}
                    >
                      Loading transactions...
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                        maxWidth: 300,
                        textAlign: 'center'
                      }}
                    >
                      Please wait while we fetch the latest time data
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : filteredEntries.length === 0 ? (
              <TableRow>
                <TableCell 
                  colSpan={(user.role === 'admin' || user.role === 'project_manager' || user.role === 'team_leader' || user.role === 'cmo' ? 8 : 7) + 1}
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    border: 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Typography
                      variant="h6" 
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                        fontWeight: 500
                      }}
                    >
                      No transactions found
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                        maxWidth: 300,
                        textAlign: 'center'
                      }}
                    >
                      Try adjusting your filters or add a new entry
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : paginatedFilteredEntries.map((e) => (
              <TableRow
                key={e.id}
                hover
                sx={{
                  borderBottom: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                  }
                }}
              >
                {(user.role === 'admin' || user.role === 'project_manager' || user.role === 'team_leader' || user.role === 'cmo') && <TableCell sx={{ 
                  color: theme.palette.mode === 'dark' ? 'white' : '#2C3034',
                  fontWeight: 500
                }}>{e.user?.name}</TableCell>}
                <TableCell sx={{ 
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                  fontWeight: 400
                }}>{e.task?.Project?.name}</TableCell>
                <TableCell sx={{ 
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                  fontWeight: 400
                }}>{e.fileName}</TableCell>
                <TableCell sx={{ 
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                  fontWeight: 400
                }}>{e.date}</TableCell>
                <TableCell sx={{ 
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                  fontWeight: 400
                }}>{e.task?.title}</TableCell>
                <TableCell sx={{
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                  fontWeight: 400
                }}>
                  {(() => {
                    // Detect edits: prefer explicit `edited` flag from backend,
                    // fall back to comparing updatedAt vs createdAt.
                    const explicitlyEdited = e.edited === true;
                    let derivedEdited = false;
                    if (!explicitlyEdited && e.createdAt && e.updatedAt) {
                      const created = new Date(e.createdAt).getTime();
                      const updated = new Date(e.updatedAt).getTime();
                      // Only flag as edited if updated significantly after create (>5s tolerance for save timing)
                      derivedEdited = updated - created > 5000;
                    }
                    const isEdited = explicitlyEdited || derivedEdited;
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                        <span>{e.hours}</span>
                        {isEdited && (
                          <Tooltip
                            title={
                              e.editedBy
                                ? `Edited by ${e.editedBy}${e.editedAt ? ' on ' + new Date(e.editedAt).toLocaleString() : ''}`
                                : (e.updatedAt ? `Last edited ${new Date(e.updatedAt).toLocaleString()}` : 'Time entry edited')
                            }
                          >
                            <Typography
                              component="span"
                              sx={{
                                fontSize: '0.7rem',
                                fontStyle: 'italic',
                                color: 'text.secondary',
                                cursor: 'help',
                              }}
                            >
                              (edited)
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell sx={{
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                  fontWeight: 400
                }}>{e.transactions}</TableCell>
                <TableCell sx={{ 
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                  fontWeight: 400
                }}>{e.transactionType}</TableCell>
                <TableCell sx={{
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#2C3034',
                  fontWeight: 400
                }}>
                  {canEdit && (
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(e)}
                        sx={{
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#2C3034',
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                          }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {(canDelete || e.userId === user?.id) && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(e)}
                        sx={{
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#2C3034',
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                            color: theme.palette.error.main,
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={Math.max(0, filteredEntries.length)}
        page={Math.max(0, Math.min(page, Math.max(0, Math.ceil(filteredEntries.length / rowsPerPage) - 1)))}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={Math.max(1, Math.min(100, rowsPerPage))}
        onRowsPerPageChange={(event) => {
          const newLimit = parseInt(event.target.value, 10);
          if (isNaN(newLimit) || newLimit <= 0) return;
          setRowsPerPage(newLimit);
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        showFirstButton
        showLastButton
      />
      </>
      )}
      </>
      )}
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        message={snackbar.message}
        action={
          <IconButton size="small" onClick={() => setSnackbar({ ...snackbar, open: false })}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this time entry?
          </DialogContentText>
          {entryToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Employee:</strong> {entryToDelete.user?.name || 'Unknown'}<br/>
                <strong>Project:</strong> {entryToDelete.task?.Project?.name || 'N/A'}<br/>
                <strong>Task:</strong> {entryToDelete.task?.title || 'N/A'}<br/>
                <strong>Date:</strong> {entryToDelete.date}<br/>
                <strong>Hours:</strong> {entryToDelete.hours}<br/>
                <strong>Transactions:</strong> {entryToDelete.transactions}<br/>
                <strong>Type:</strong> {entryToDelete.transactionType || 'N/A'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Time Entry Modal */}
      <Dialog 
        open={addEntryOpen} 
        onClose={(event, reason) => {
          // Only close on explicit actions (Cancel button, etc.), not on backdrop click or Escape key
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleAddEntryClose();
          }
        }}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>{editingId ? 'Edit Time Entry' : 'Add New Time Entry'}</DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <Box display="flex" flexDirection="column" gap={3} sx={{ mt: 2 }}>
            {(user.role === 'admin' || user.role === 'project_manager' || user.role === 'team_leader' || user.role === 'cmo') && !isFieldHidden('userId') && (
              <Autocomplete
                fullWidth
                options={users}
                getOptionLabel={(option) => option.name}
                value={users.find(u => u.id === parseInt(form.userId)) || null}
                onChange={(event, newValue) => {
                  const updatedForm = { ...form, userId: newValue ? newValue.id : '' };
                  // Clear task, project, and transaction type when employee changes
                  updatedForm.taskId = '';
                  updatedForm.transactionType = '';
                  setSelectedProjectId('');
                  setForm(updatedForm);
                  setTransactionTypes([]);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employee"
                    placeholder="Search for employee..."
                    fullWidth
                  />
                )}
              />
            )}
            <Autocomplete
              fullWidth
              options={getProjectOptions()}
              getOptionLabel={(option) => option.name}
              value={getProjectOptions().find(p => p.id === parseInt(selectedProjectId)) || null}
              onChange={(event, newValue) => {
                // Business rule: block selecting a completed project
                if (newValue && (newValue.status || '').toLowerCase() === 'completed') {
                  setSnackbar({
                    open: true,
                    message: `"${newValue.name}" is completed — no new time entries can be added.`,
                  });
                  return;
                }
                setSelectedProjectId(newValue ? newValue.id : '');
                // Clear task and transaction type when project changes
                setForm({ ...form, taskId: '', transactionType: '' });
                setTransactionTypes([]);
              }}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    opacity: (option.status || '').toLowerCase() === 'completed' ? 0.6 : 1,
                  }}
                >
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {option.name}
                  </Box>
                  {(option.status || '').toLowerCase() === 'completed' && (
                    <Box
                      component="span"
                      sx={{
                        px: 0.75,
                        py: 0.15,
                        bgcolor: '#f3f4f6',
                        color: '#6b7280',
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        borderRadius: 0.75,
                        letterSpacing: 0,
                        flexShrink: 0,
                      }}
                    >
                      Completed
                    </Box>
                  )}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project"
                  placeholder="Search for project..."
                  fullWidth
                />
              )}
            />
            {!isFieldHidden('taskId') && (
            <Autocomplete
              fullWidth
              options={getFilteredTasks()}
              getOptionLabel={(option) => `${option.title} (${option.Project?.name || 'No Project'})`}
              value={getFilteredTasks().find(t => t.id === parseInt(form.taskId)) || null}
              onChange={(event, newValue) => {
                  const taskId = newValue ? newValue.id : '';
                  
                  // Auto-populate transactionType from task if it exists, otherwise clear it
                  const taskTransactionType = newValue?.transactionType && newValue.transactionType.trim() !== '' 
                    ? newValue.transactionType.trim() 
                    : '';
                  
                  const updatedForm = { ...form, taskId, transactionType: taskTransactionType };
                  
                  console.log('[TimeTransactions] Task selected in Autocomplete:', taskId);
                  console.log('[TimeTransactions] Selected task object:', newValue);
                  console.log('[TimeTransactions] Task transactionType:', newValue?.transactionType);
                  console.log('[TimeTransactions] Task Project:', newValue?.Project);
                  console.log('[TimeTransactions] Task Project serviceId:', newValue?.Project?.serviceId);
                  
                  if (newValue?.Project?.serviceId) {
                    console.log('[TimeTransactions] Task has project with serviceId, fetching transaction types');
                    fetchTransactionTypes(newValue.Project.serviceId);
                  } else {
                    console.log('[TimeTransactions] Task does not have project serviceId, clearing transaction types');
                    setTransactionTypes([]);
                  }
                  
                  setForm(updatedForm);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Task"
                  placeholder="Search for task..."
                  fullWidth
                />
              )}
            />
            )}
            <Box display="flex" gap={2}>
              {!isFieldHidden('date') && (
              <TextField
                name="date"
                label="Date *"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.date}
                onChange={handleChange}
                required
                error={!!formDateError}
                helperText={formDateError}
                inputProps={{
                  // Restrict to today or past dates (local date; toISOString would use UTC)
                  max: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })()
                }}
              />
              )}
              {!isFieldHidden('hours') && (
              <TextField
                name="hours"
                label="Hours *"
                type="number"
                fullWidth
                value={form.hours}
                onChange={handleChange}
                required
              />
              )}
            </Box>
            {!isFieldHidden('fileName') && (
            <TextField
              name="fileName"
              label={fileAndTxOptional ? "File Name / Description" : "File Name / Description *"}
              fullWidth
              value={form.fileName}
              onChange={handleChange}
              required={!fileAndTxOptional}
            />
            )}
            <Box display="flex" gap={2}>
              {!isFieldHidden('transactions') && (
              <TextField
                name="transactions"
                label={fileAndTxOptional ? "Transactions" : "Transactions *"}
                type="number"
                fullWidth
                value={form.transactions}
                onChange={handleChange}
                required={!fileAndTxOptional}
              />
              )}
              {!isFieldHidden('transactionType') && (
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                  <Select 
                    name="transactionType" 
                    value={(() => {
                      // Case-insensitive matching for transaction type
                      if (!form.transactionType) return '';
                      if (transactionTypes.length === 0) return form.transactionType; // Return original if no types loaded yet
                      
                      // Find case-insensitive match
                      const matchedType = transactionTypes.find(
                        type => type.transactiontype?.toLowerCase() === form.transactionType?.toLowerCase()
                      );
                      
                      // If found, return the exact value from database (to normalize casing)
                      // If not found, return original value (for old entries that might not be in DB)
                      return matchedType ? matchedType.transactiontype : form.transactionType;
                    })()}
                    label="Type" 
                    onChange={handleChange}
                    disabled={loadingTransactionTypes || !form.taskId}
                  >
                    <MenuItem value="">None</MenuItem>
                    {loadingTransactionTypes ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : transactionTypes.length > 0 ? (
                      transactionTypes.map((type) => (
                        <MenuItem key={type.id} value={type.transactiontype}>
                          {type.transactiontype}
                        </MenuItem>
                      ))
                    ) : form.taskId ? (
                      <MenuItem disabled>No transaction types available for this service</MenuItem>
                    ) : (
                      <MenuItem disabled>Select a task first</MenuItem>
                    )}
                    {/* Add the current form value as an option if it's not in the transactionTypes list (for old entries) */}
                    {form.transactionType && transactionTypes.length > 0 && 
                     !transactionTypes.some(type => type.transactiontype?.toLowerCase() === form.transactionType?.toLowerCase()) && (
                      <MenuItem value={form.transactionType}>
                        {form.transactionType}
                      </MenuItem>
                    )}
                </Select>
              </FormControl>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddEntryClose}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={async () => {
              const success = await handleSubmit();
              if (success) {
                handleAddEntryClose();
              }
            }}
            disabled={loading}
          >
            {loading ? 'Saving...' : (editingId ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add many entries: type, paste from Excel, or import a file */}
      {bulkGridOpen && (
        <React.Suspense fallback={null}>
        <BulkGrid
          open
          onClose={() => setBulkGridOpen(false)}
          title="Add many time entries"
          subtitle="Type, paste a block from Excel or Google Sheets, or import a .xlsx / .csv file. Everything is checked before it is added."
          columns={bulkColumns}
          validateRow={validateTimeRow}
          dedupeKey={timeDedupeKey}
          dryRun={bulkDryRun}
          onSubmit={bulkSubmit}
          submitLabel={(n) => `Add ${n} entr${n === 1 ? 'y' : 'ies'}`}
          templateName="time_transactions_template.csv"
        />
        </React.Suspense>
      )}
      </Box>
    </Box>
  );
};

export default TimeTransactions;